/**
 * The one shape every write in the console takes.
 *
 * Reads got a single seam (`fixtures/hooks.ts`) and writes need the same, for a
 * blunter reason: a write that fails must SAY so. The failure path is the one
 * nobody exercises by hand, so if each button invents its own, the ones that are
 * wrong are exactly the ones nobody notices — a suspend that silently did
 * nothing looks identical to a suspend that worked.
 *
 * Three things are therefore not left to the call site:
 *
 * 1. **The server's own message wins.** `ApiRequestError.message` is written for
 *    the person reading it and already carries the reason a refusal happened.
 *    Replacing it with "Something went wrong" throws that away.
 * 2. **A refusal and a dead connection read differently.** "The server said no"
 *    and "you are offline" call for different actions from whoever is looking.
 * 3. **What changed is refetched.** A recorded payment moves the invoice, the
 *    subscription, the business and the overview's revenue tile. Anything not
 *    invalidated keeps rendering the figure from before the write.
 */
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query'

import { useToast } from '../components/Toast'
import { ApiRequestError, ApiTransportError } from './apiClient'
import { t } from './i18n'

/** The words a failed write puts in front of whoever pressed the button. */
export function messageForError(cause: unknown): string {
  if (cause instanceof ApiTransportError) return t('state.offlineTitle')
  if (cause instanceof ApiRequestError) return cause.message
  return t('state.errorTitle')
}

export interface ApiMutationOptions<TArgs, TResult> {
  /** The call itself. Throwing is how it fails; there is no result flag. */
  run: (args: TArgs) => Promise<TResult>
  /**
   * Refetched on success. Be generous rather than precise: a stale figure on a
   * screen the user is looking at costs more than one extra request.
   */
  invalidates?: QueryKey[]
  /**
   * Everything. For a write whose effect cannot be enumerated — a plan price
   * change moves what every tenant is billed — this is the honest option.
   */
  invalidateAll?: boolean
  /** Shown on success. Omit for a write whose own screen already says it. */
  success?: string | ((result: TResult, args: TArgs) => string)
  onDone?: (result: TResult, args: TArgs) => void
}

/**
 * A write, with its toast and its invalidation attached.
 *
 * Returns TanStack's mutation object unchanged, so a caller still gets
 * `isPending` for the button's disabled state — a second click on a suspend
 * button is a second audit row.
 */
export function useApiMutation<TArgs = void, TResult = unknown>(
  options: ApiMutationOptions<TArgs, TResult>,
) {
  const queryClient = useQueryClient()
  const toast = useToast()

  return useMutation<TResult, unknown, TArgs>({
    mutationFn: options.run,
    onSuccess: async (result, args) => {
      if (options.invalidateAll) {
        await queryClient.invalidateQueries()
      } else if (options.invalidates?.length) {
        await Promise.all(
          options.invalidates.map((key) => queryClient.invalidateQueries({ queryKey: key })),
        )
      }
      if (options.success !== undefined) {
        toast.show(
          typeof options.success === 'function' ? options.success(result, args) : options.success,
        )
      }
      options.onDone?.(result, args)
    },
    onError: (cause) => toast.show(messageForError(cause), 'danger'),
  })
}
